import { useInfiniteQuery } from '@tanstack/react-query';
import { Interval } from 'luxon';
import { useIntl } from 'react-intl';

import { getReviewsInsightForEntity } from 'apis/merchant-gateway/insights/reviews/api';
import { RatingFilterValues, ReviewsTableBackendSortableIds } from 'apis/merchant-gateway/insights/reviews/types';
import { EntityType } from 'common/types';
import { InfiniteTable } from 'modules/experience-core';

type ReviewsInsightParams = {
  entityType: EntityType;
  entityId: string;
  interval: Interval;
  filters: RatingFilterValues[];
  cursor?: string;
  sortBy?: ReviewsTableBackendSortableIds;
  sortOrder?: InfiniteTable.SortOrder;
  limit?: number;
};

const isVenueEntity = (entityType: EntityType): entityType is 'venue' => entityType === 'venue';

const useReviewsInsights = ({
  entityType,
  entityId,
  interval,
  filters,
  sortBy,
  sortOrder,
  limit,
}: ReviewsInsightParams) => {
  const { locale } = useIntl();

  const isEnabled = isVenueEntity(entityType);
  return useInfiniteQuery(
    ['reviews', entityType, entityId, interval, filters, sortBy, sortOrder, limit, locale],
    ({ pageParam: cursor }) => {
      if (!isEnabled) {
        throw new Error('ReviewsInsight not for venue entity is not supported');
      }

      return getReviewsInsightForEntity({
        entityType,
        entityId,
        interval,
        filters,
        cursor,
        sortBy,
        sortOrder,
        limit,
        locale,
      });
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      getNextPageParam: lastPage => lastPage.pagination?.next,
      keepPreviousData: true,
      enabled: isEnabled,
    },
  );
};

export default useReviewsInsights;
